import CacheUtil from './cache'

/**
 * 缓存管理工具类
 * 提供缓存清理、统计、调试等功能
 */
class CacheManager {
  /**
   * 清理所有缓存
   */
  static clearAll() {
    CacheUtil.clear('all');
    console.log('所有缓存已清理');
  }

  /**
   * 清理指定前缀的缓存
   * @param {string} prefix 缓存键前缀
   * @param {string} storage 存储类型
   */
  static clearByPrefix(prefix, storage = 'local') {
    const keys = CacheUtil.getKeys(storage);
    let clearedCount = 0;

    keys.forEach(key => {
      if (key.startsWith(prefix)) {
        CacheUtil.remove(key, storage);
        clearedCount++;
      }
    });
    console.log(`已清理 ${clearedCount} 个以 "${prefix}" 开头的缓存`);
  }

  /**
   * 清理首页相关缓存
   */
  static clearHomeCache() {
    this.clearByPrefix('home_');
    this.clearByPrefix('online_');
    this.clearByPrefix('area_');
    this.clearByPrefix('friend_');
    this.clearByPrefix('swiper_');
    this.clearByPrefix('mall_');
    this.clearByPrefix('good_supplier_');
  }

  /**
   * 获取缓存统计信息
   * @param {string} storage 存储类型
   */
  static getStats(storage = 'local') {
    return CacheUtil.getStats(storage);
  }

  /**
   * 打印缓存统计信息
   * @param {string} storage 存储类型
   */
  static printStats(storage = 'local') {
    const stats = this.getStats(storage);
    console.log('=== 缓存统计信息 ===');
    console.log(`存储类型: ${storage}`);
    console.log(`总键数: ${stats.totalKeys}`);
    console.log(`有效缓存: ${stats.validCount}`);
    console.log(`过期缓存: ${stats.expiredCount}`);
    console.log(`总大小: ${stats.totalSize}`);
    console.log('==================');
  }

  /**
   * 获取所有缓存键名
   * @param {string} storage 存储类型
   */
  static getAllKeys(storage = 'local') {
    return CacheUtil.getKeys(storage);
  }

  /**
   * 打印所有缓存键名
   * @param {string} storage 存储类型
   */
  static printAllKeys(storage = 'local') {
    const keys = this.getAllKeys(storage);
    console.log(`=== ${storage} 存储的所有缓存键 ===`);
    keys.forEach((key, index) => {
      const info = CacheUtil.getInfo(key, storage);
      if (info) {
        console.log(`${index + 1}. ${key} - 剩余时间: ${info.remainingTime ? Math.floor(info.remainingTime / 1000) + 's' : '永久'}`);
      } else {
        console.log(`${index + 1}. ${key} - 已过期或无效`);
      }
    });
    console.log('================================');
  }

  /**
   * 强制刷新指定缓存
   * @param {string} key 缓存键名
   * @param {string} storage 存储类型
   */
  static forceRefresh(key, storage = 'local') {
    CacheUtil.remove(key, storage);
    console.log(`缓存 "${key}" 已强制刷新`);
  }

  /**
   * 设置缓存调试模式
   * @param {boolean} enabled 是否启用调试模式
   */
  static setDebugMode(enabled = true) {
    if (enabled) {
      console.log('缓存调试模式已启用');
      // 定期打印缓存统计信息
      this.debugInterval = setInterval(() => {
        this.printStats();
      }, 30000); // 每30秒打印一次
    } else {
      if (this.debugInterval) {
        clearInterval(this.debugInterval);
        this.debugInterval = null;
        console.log('缓存调试模式已关闭');
      }
    }
  }

  /**
   * 获取缓存使用建议
   */
  static getUsageTips() {
    const stats = this.getStats();
    const tips = [];

    if (stats.totalKeys > 50) {
      tips.push('缓存键数量较多，建议清理不必要的缓存');
    }

    if (stats.expiredCount > 10) {
      tips.push('有过期缓存，建议清理过期缓存');
    }

    const sizeInKB = parseFloat(stats.totalSize);
    if (sizeInKB > 1024) { // 大于1MB
      tips.push('缓存总大小较大，建议清理部分缓存');
    }

    return tips;
  }

  /**
   * 自动清理过期缓存
   */
  static autoCleanup() {
    CacheUtil.clearExpired('local');
    CacheUtil.clearExpired('session');
    console.log('过期缓存自动清理完成');
  }

  /**
   * 初始化缓存管理
   */
  static init() {
    // 定期清理过期缓存
    setInterval(() => {
      this.autoCleanup();
    }, 60000); // 每分钟清理一次

    // 在页面卸载时清理过期缓存
    window.addEventListener('beforeunload', () => {
      this.autoCleanup();
    });

    console.log('缓存管理器已初始化');
  }
}

// 在开发环境下启用调试模式
if (process.env.NODE_ENV === 'development') {
  // 将缓存管理器挂载到全局，方便调试
  window.CacheManager = CacheManager;

  // 自动初始化
  CacheManager.init();
}

export default CacheManager;
